import { useEffect, useMemo, useRef, useState } from "react";
import {
  GuestPhoto,
  CountdownSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import type { VariantProps } from "./types";

// ═══════════════════════════════════════════════════════════════════════════
// 5. MINIMAL — Oqshom (w05) — single-page card, just the essentials
//
// Reference DNA: Aesop / Acne Studios / Papier / Are.na editorial.
// Brand promise: restraint. Negative space is the design. One accent (sage),
// one ink, one cream paper. No ornaments, no gradients, no textures, no
// decorations — by deliberate omission.
//
// Things this template does NOT add:
//   • No ornaments (no diamonds, no flourishes, no SVG decorations on the page).
//   • No gradients or textures.
//   • No multiple accent colors.
//   • No section reveal sounds.
//   • No long animations — fades cap at 300ms.
//   • No filler photo grids — Oqshom only has 2 photos and we honour that.
//
// Signature element: the date "14.09.2026" rendered as enormous tabular
// numerals across the bottom of the hero — treated as architecture, not data.
// ═══════════════════════════════════════════════════════════════════════════

// ─── tiny IO-fade hook ────────────────────────────────────────────────────
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof window === "undefined") return;
    const reduce =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setShown(true);
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, shown };
}

// ─── reveal style helper ──────────────────────────────────────────────────
const revealStyle = (shown: boolean): React.CSSProperties => ({
  opacity: shown ? 1 : 0,
  transform: shown ? "translateY(0)" : "translateY(8px)",
  transition: "opacity 300ms ease, transform 300ms ease",
});

// ─── Khiva walled-city silhouette — bottom-right hero accent at 8% opacity ─
// Restrained. Single thin stroke. No fills. Reads as architecture, not motif.
function KhivaSilhouette({ color }: { color: string }) {
  return (
    <svg
      viewBox="0 0 480 80"
      preserveAspectRatio="xMaxYEnd meet"
      style={{ width: "100%", height: "100%", display: "block" }}
      aria-hidden="true"
    >
      <g fill="none" stroke={color} strokeWidth="1" opacity="0.85">
        {/* baseline */}
        <line x1="0" y1="78" x2="480" y2="78" />
        {/* low wall */}
        <path d="M0 78 L0 60 L40 60 L40 70 L80 70 L80 56 L130 56 L130 64 L170 64 L170 50 L220 50 L220 60 L250 60 L250 78" />
        {/* tall minaret */}
        <path d="M250 78 L250 22 L256 18 L256 10 L260 6 L264 10 L264 18 L270 22 L270 78" />
        <line x1="252" y1="34" x2="268" y2="34" />
        {/* dome */}
        <path d="M285 78 L285 50 Q 305 28 325 50 L 325 78" />
        {/* second wall */}
        <path d="M325 78 L325 58 L370 58 L370 66 L410 66 L410 52 L450 52 L450 62 L480 62 L480 78" />
        {/* battlement teeth on right wall */}
        <path d="M370 58 L370 54 L378 54 L378 58 M386 58 L386 54 L394 54 L394 58 M402 58 L402 54 L410 54 L410 58" />
      </g>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export function VariantMinimal({ t, isDark, ctx }: VariantProps) {
  const p = t.photos || [];
  const [first, second] = t.names.split(" & ");
  const { play } = useSound();
  const tt = useT();

  // Prepare a clean tabular representation of the date — the hero architecture.
  const dateChars = useMemo(() => t.date.split(""), [t.date]);

  // Reveal nodes — sharp, fast (300ms), small translateY.
  const heroR = useReveal<HTMLDivElement>();
  const stripR = useReveal<HTMLDivElement>();
  const scheduleR = useReveal<HTMLDivElement>();
  const colophonR = useReveal<HTMLDivElement>();

  // Schedule items — numbered list (01–04), thin metric typography, vertical rule.
  const schedule: Array<[string, string, string]> = [
    [tt("variant.w05.schedule.0.num"), tt("variant.w05.schedule.0.time"), tt("variant.w05.schedule.0.event")],
    [tt("variant.w05.schedule.1.num"), tt("variant.w05.schedule.1.time"), tt("variant.w05.schedule.1.event")],
    [tt("variant.w05.schedule.2.num"), tt("variant.w05.schedule.2.time"), tt("variant.w05.schedule.2.event")],
    [tt("variant.w05.schedule.3.num"), tt("variant.w05.schedule.3.time"), tt("variant.w05.schedule.3.event")],
  ];

  // Scroll-to-RSVP with a quiet `tap` — restraint: 1 of 2 sound triggers.
  const onScrollToRsvp = () => {
    play("tap");
    if (typeof window === "undefined") return;
    const el = document.getElementById("oqshom-rsvp");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Save-the-Date `.ics` button — quiet `click` — 2 of 2 sound triggers.
  const onSaveDate = () => {
    play("click");
    // Build an RFC-5545 .ics from the template date — pure client-side, no deps.
    if (typeof window === "undefined") return;
    const [d, m, y] = t.date.split(".");
    if (!d || !m || !y) return;
    const stamp = `${y}${m.padStart(2, "0")}${d.padStart(2, "0")}`;
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//taklifla//oqshom//EN",
      "BEGIN:VEVENT",
      `UID:oqshom-${stamp}@taklifla.uz`,
      `DTSTAMP:${stamp}T000000Z`,
      `DTSTART;VALUE=DATE:${stamp}`,
      `SUMMARY:${t.names}`,
      `LOCATION:${t.venue}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `oqshom-${stamp}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="oqshom-variant">
      {/* ─── Mobile refinements — desktop styles preserved, only @media (max-width: 768px) overrides below.
            The discipline of restraint > the temptation to fill the smaller screen. ─── */}
      <style>{`
        .oqshom-variant { display: contents; }
        @media (max-width: 768px) {
          /* Hero — 12-col → 4-col mobile grid. Names anchor at col 1, date strip spans col 2-4. */
          .oqshom-variant .oqshom-hero {
            grid-template-columns: repeat(4, 1fr) !important;
            column-gap: clamp(4px, 1.5vw, 12px) !important;
            row-gap: clamp(32px, 7vw, 56px) !important;
            padding: 64px 18px 48px !important;
          }
          .oqshom-variant .oqshom-hero-photo-frame {
            height: min(60vh, 560px) !important;
          }
          /* Top metadata strip — span full width, smaller micro-type. */
          .oqshom-variant .oqshom-meta-top {
            grid-column: 1 / -1 !important;
            gap: 10px !important;
          }
          .oqshom-variant .oqshom-meta-top span:first-child,
          .oqshom-variant .oqshom-meta-top span:last-child {
            font-size: 9px !important;
            letter-spacing: 0.36em !important;
          }
          /* Hero photo block — full-bleed edge-to-edge, breaks out of the page padding.
             16:9 on mobile (negative-space-defying — the one place we let it breathe). */
          .oqshom-variant .oqshom-hero-photo {
            grid-column: 1 / -1 !important;
            margin-left: calc(-1 * clamp(16px, 5vw, 28px)) !important;
            margin-right: calc(-1 * clamp(16px, 5vw, 28px)) !important;
          }
          .oqshom-variant .oqshom-hero-photo-frame {
            aspect-ratio: 16 / 9 !important;
          }
          /* Names — anchor at col 1, span full 4-col width, mobile clamp. */
          .oqshom-variant .oqshom-names {
            grid-column: 1 / -1 !important;
          }
          .oqshom-variant .oqshom-names .oqshom-name-line {
            font-size: clamp(36px, 12vw, 72px) !important;
            font-feature-settings: "lnum", "ss01" !important;
            font-variation-settings: "wght" 380 !important;
            line-height: 0.86 !important;
          }
          .oqshom-variant .oqshom-names .oqshom-amp {
            font-size: 0.5em !important;
          }
          /* Venue/place metadata — full width, tighter wrap. */
          .oqshom-variant .oqshom-place {
            grid-column: 1 / -1 !important;
            gap: 8px !important;
            padding-top: 12px !important;
          }
          .oqshom-variant .oqshom-place > span {
            font-size: 9px !important;
            letter-spacing: 0.32em !important;
          }
          .oqshom-variant .oqshom-place .oqshom-place-italic {
            font-size: 12px !important;
            letter-spacing: 0.01em !important;
          }
          /* Architectural date — KEEP signature. Date strip spans col 2-4. */
          .oqshom-variant .oqshom-date {
            grid-column: 2 / -1 !important;
            padding-top: 8px !important;
          }
          .oqshom-variant .oqshom-date .oqshom-date-glyph {
            font-size: clamp(48px, 16vw, 88px) !important;
            font-feature-settings: "lnum", "tnum" !important;
            line-height: 0.84 !important;
            letter-spacing: -0.04em !important;
          }
          /* CTA — sticky-prominent .ics save button: full-width, 48px tall. */
          .oqshom-variant .oqshom-cta {
            grid-column: 1 / -1 !important;
            flex-direction: column !important;
            gap: 10px !important;
          }
          .oqshom-variant .oqshom-cta button {
            width: 100% !important;
            height: 48px !important;
            padding: 0 18px !important;
            font-size: 10px !important;
            letter-spacing: 0.36em !important;
          }
          /* Khiva silhouette — anchor bottom-left, 12% opacity to balance date glyphs. */
          .oqshom-variant .oqshom-khiva {
            right: auto !important;
            left: clamp(12px, 4vw, 24px) !important;
            bottom: clamp(8px, 2vw, 18px) !important;
            width: min(220px, 56vw) !important;
            height: min(40px, 9vw) !important;
            opacity: 0.12 !important;
          }
          /* Invitation copy — single column on mobile. */
          .oqshom-variant .oqshom-invite-grid {
            grid-template-columns: repeat(4, 1fr) !important;
          }
          .oqshom-variant .oqshom-invite-mark {
            grid-column: 1 / -1 !important;
            padding-top: 0 !important;
            margin-bottom: 8px !important;
          }
          .oqshom-variant .oqshom-invite-copy {
            grid-column: 1 / -1 !important;
            font-size: clamp(20px, 5.4vw, 28px) !important;
            line-height: 1.3 !important;
          }
          /* Photo strip section — single column stacked. */
          .oqshom-variant .oqshom-strip {
            grid-template-columns: 1fr !important;
            gap: clamp(12px, 3vw, 20px) !important;
          }
          .oqshom-variant .oqshom-strip > div:last-child {
            margin-top: 0 !important;
          }
          /* Schedule — single column, 56px tap targets, tabular time right-aligned. */
          .oqshom-variant .oqshom-schedule-grid {
            grid-template-columns: repeat(4, 1fr) !important;
          }
          .oqshom-variant .oqshom-schedule-head,
          .oqshom-variant .oqshom-schedule-list {
            grid-column: 1 / -1 !important;
          }
          .oqshom-variant .oqshom-schedule-head h2 {
            font-size: clamp(40px, 11vw, 64px) !important;
          }
          .oqshom-variant .oqshom-schedule-list li {
            min-height: 56px !important;
            padding: 14px 0 14px 14px !important;
            grid-template-columns: auto 1fr auto !important;
            gap: 14px !important;
          }
          .oqshom-variant .oqshom-schedule-list li > span:nth-child(1) {
            font-size: 10px !important;
            letter-spacing: 0.28em !important;
          }
          .oqshom-variant .oqshom-schedule-list li > span:nth-child(2) {
            font-size: clamp(18px, 4.6vw, 24px) !important;
            font-style: italic !important;
          }
          .oqshom-variant .oqshom-schedule-list li > span:nth-child(3) {
            font-size: clamp(16px, 4.2vw, 22px) !important;
            font-feature-settings: "lnum", "tnum" !important;
            text-align: right !important;
          }
          /* Colophon — 2x2 grid (4 items, 2 cols), mono micro-type. */
          .oqshom-variant .oqshom-colophon-grid {
            grid-template-columns: repeat(4, 1fr) !important;
          }
          .oqshom-variant .oqshom-colophon-mark {
            grid-column: 1 / -1 !important;
            margin-bottom: 12px !important;
          }
          .oqshom-variant .oqshom-colophon-dl {
            grid-column: 1 / -1 !important;
            grid-template-columns: 1fr 1fr !important;
            row-gap: 14px !important;
            column-gap: 14px !important;
            font-size: 9px !important;
            letter-spacing: 0.28em !important;
          }
          .oqshom-variant .oqshom-colophon-dl dt {
            display: block;
          }
          .oqshom-variant .oqshom-colophon-dl dd {
            display: block;
            margin-top: 4px !important;
          }
        }
        /* Touch-only: tap toggles hero photo from grayscale to colour
           (desktop hover already handled by HeroPhoto component). */
        @media (hover: none) {
          .oqshom-variant .oqshom-hero-photo .oqshom-hero-photo-inner:active {
            filter: grayscale(0) contrast(1) !important;
            transition: filter 600ms ease !important;
          }
        }
      `}</style>
      {/* ────────────────────────────────────────────────────────────────────
          HERO — asymmetric 12-col grid, photo as wide cinematic strip,
          enormous tabular numerals as architecture.
          ──────────────────────────────────────────────────────────────────── */}
      <section
        ref={heroR.ref}
        className="oqshom-hero"
        style={{
          position: "relative",
          minHeight: "100vh",
          padding: "clamp(48px, 8vw, 120px) clamp(20px, 6vw, 80px)",
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          columnGap: "clamp(8px, 1.5vw, 24px)",
          alignContent: "space-between",
          rowGap: "clamp(48px, 8vw, 96px)",
          fontFeatureSettings: '"lnum", "ss01"',
        }}
      >
        {/* Top metadata strip — col 1 .. 12, single hairline, micro mono labels */}
        <div
          className="oqshom-meta-top"
          style={{
            gridColumn: "1 / -1",
            display: "flex",
            alignItems: "center",
            gap: 16,
            ...revealStyle(heroR.shown),
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.42em",
              color: t.ink,
            }}
          >
            {tt("variant.w05.meta.label")}
          </span>
          <span style={{ flex: 1, height: 1, background: `${t.ink}1f` }} />
          <span
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.42em",
              color: t.accent,
            }}
          >
            {tt("variant.w05.meta.year")}
          </span>
        </div>

        {/* Photo — wide cinematic strip 21:9, spans col 2..12, asymmetric.
            Desktop hover: subtle grayscale-to-color via 3s ease, gated by
            (hover: hover) and (pointer: fine). Implemented inline below
            with onMouseEnter/Leave because we have no global CSS. */}
        <div
          className="oqshom-hero-photo"
          style={{
            gridColumn: "2 / -1",
            ...revealStyle(heroR.shown),
            transitionDelay: "60ms",
          }}
        >
          <HeroPhoto src={p[0]} fallbackBg={t.bg} fallbackTone={t.accent} />
        </div>

        {/* Names — start at col 2, italic ampersand, surgical line-height. */}
        <div
          className="oqshom-names"
          style={{
            gridColumn: "2 / span 9",
            ...revealStyle(heroR.shown),
            transitionDelay: "120ms",
          }}
        >
          <div
            className="oqshom-name-line"
            style={{
              fontFamily: t.font,
              fontSize: "clamp(56px, 12vw, 168px)",
              lineHeight: 0.84,
              fontWeight: 400,
              letterSpacing: "-0.045em",
              color: t.ink,
              fontVariationSettings: '"wght" 420',
              fontFeatureSettings: '"lnum", "ss01"',
            }}
          >
            {first}
          </div>
          <div
            className="oqshom-name-line"
            style={{
              fontFamily: t.font,
              fontSize: "clamp(56px, 12vw, 168px)",
              lineHeight: 0.84,
              fontWeight: 400,
              letterSpacing: "-0.045em",
              color: t.ink,
              display: "flex",
              alignItems: "baseline",
              gap: "0.12em",
              marginTop: 4,
            }}
          >
            <span
              className="oqshom-amp"
              style={{
                fontStyle: "italic",
                color: t.accent,
                fontSize: "0.94em",
              }}
            >
              &amp;
            </span>
            <span style={{ fontVariationSettings: '"wght" 420' }}>{second || ""}</span>
          </div>
        </div>

        {/* Single-line metadata strip — venue (left) · "Khiva — the walled city" */}
        <div
          className="oqshom-place"
          style={{
            gridColumn: "2 / -1",
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
            paddingTop: 18,
            borderTop: `1px solid ${t.ink}1f`,
            ...revealStyle(heroR.shown),
            transitionDelay: "180ms",
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.42em",
              color: t.ink,
            }}
          >
            {t.venue.toUpperCase()}
          </span>
          <span
            className="oqshom-place-italic"
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: 14,
              color: `${t.ink}aa`,
              letterSpacing: "0.01em",
            }}
          >
            {tt("variant.w05.meta.tagline")}
          </span>
          <span
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.42em",
              color: t.accent,
            }}
          >
            {tt("variant.w05.meta.country")}
          </span>
        </div>

        {/* SIGNATURE — date as architecture. Enormous tabular numerals,
            tightly tracked, broken across columns. Each glyph in its own
            cell so the dot separators sit on the grid lines. */}
        <div
          className="oqshom-date"
          style={{
            gridColumn: "1 / -1",
            display: "grid",
            gridTemplateColumns: `repeat(${dateChars.length}, 1fr)`,
            alignItems: "end",
            color: t.ink,
            paddingTop: 12,
            borderTop: `1px solid ${t.ink}1f`,
            ...revealStyle(heroR.shown),
            transitionDelay: "240ms",
          }}
          aria-label={tt("variant.w05.aria.date").replace("{date}", t.date)}
        >
          {dateChars.map((ch, i) => (
            <span
              key={i}
              className="oqshom-date-glyph"
              style={{
                fontFamily: t.font,
                fontSize: "clamp(48px, 14vw, 220px)",
                lineHeight: 0.82,
                fontWeight: 400,
                fontVariationSettings: '"wght" 360',
                fontFeatureSettings: '"lnum", "tnum"',
                letterSpacing: "-0.04em",
                textAlign: "center",
                color: ch === "." ? t.accent : t.ink,
              }}
            >
              {ch}
            </span>
          ))}
        </div>

        {/* Quiet CTA pair — restrained. */}
        <div
          className="oqshom-cta"
          style={{
            gridColumn: "2 / -1",
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            ...revealStyle(heroR.shown),
            transitionDelay: "300ms",
          }}
        >
          <button
            type="button"
            onClick={onSaveDate}
            style={{
              appearance: "none",
              background: t.ink,
              color: t.bg,
              border: `1px solid ${t.ink}`,
              padding: "14px 22px",
              fontFamily: "var(--font-ui)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.42em",
              cursor: "pointer",
            }}
          >
            {tt("variant.w05.cta.save")}
          </button>
          <button
            type="button"
            onClick={onScrollToRsvp}
            style={{
              appearance: "none",
              background: "transparent",
              color: t.ink,
              border: `1px solid ${t.ink}`,
              padding: "14px 22px",
              fontFamily: "var(--font-ui)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.42em",
              cursor: "pointer",
            }}
          >
            {tt("variant.w05.cta.rsvp")}
          </button>
        </div>

        {/* Khiva silhouette — bottom-right corner, 8% opacity, hairline only. */}
        <div
          aria-hidden="true"
          className="oqshom-khiva"
          style={{
            position: "absolute",
            right: "clamp(20px, 6vw, 80px)",
            bottom: "clamp(8px, 2vw, 24px)",
            width: "min(380px, 38vw)",
            height: "min(60px, 6vw)",
            opacity: 0.08,
            color: t.ink,
            pointerEvents: "none",
          }}
        >
          <KhivaSilhouette color="currentColor" />
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────
          INVITATION COPY — single line of italic body copy, generous space.
          ──────────────────────────────────────────────────────────────────── */}
      <section
        style={{
          padding: "clamp(72px, 12vw, 160px) clamp(20px, 6vw, 80px)",
          borderTop: `1px solid ${t.ink}1f`,
        }}
      >
        <div
          className="oqshom-invite-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            columnGap: "clamp(8px, 1.5vw, 24px)",
            alignItems: "start",
          }}
        >
          <div
            className="oqshom-invite-mark"
            style={{
              gridColumn: "2 / span 1",
              fontFamily: "var(--font-ui)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.42em",
              color: t.accent,
              paddingTop: 8,
            }}
          >
            {tt("variant.w05.invite.mark")}
          </div>
          <p
            className="oqshom-invite-copy"
            style={{
              gridColumn: "4 / span 7",
              fontFamily: t.font,
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: "clamp(22px, 3.4vw, 44px)",
              lineHeight: 1.25,
              letterSpacing: "-0.01em",
              color: t.ink,
              margin: 0,
              fontFeatureSettings: '"lnum", "ss01"',
            }}
          >
            {tt("variant.w05.invite.copy")}
          </p>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────────
          PHOTO STRIP — Oqshom only has 2 photos. Honour that with a single
          wide editorial pair, not a 3-up grid. Asymmetric proportions
          (golden-ish 1.618 : 1).
          ──────────────────────────────────────────────────────────────────── */}
      {(p[0] || p[1]) && (
        <section
          ref={stripR.ref}
          style={{
            padding: "0 clamp(20px, 6vw, 80px) clamp(72px, 12vw, 160px)",
            borderTop: `1px solid ${t.ink}1f`,
          }}
        >
          <div
            className="oqshom-strip"
            style={{
              display: "grid",
              gridTemplateColumns: "1.618fr 1fr",
              gap: "clamp(8px, 1.5vw, 24px)",
              paddingTop: "clamp(48px, 8vw, 96px)",
              ...revealStyle(stripR.shown),
            }}
          >
            <div style={{ aspectRatio: "21/9" }}>
              <GuestPhoto
                src={p[0]}
                style={{ width: "100%", height: "100%" }}
                filter="grayscale(0.35) contrast(1.02)"
                fallbackBg={t.bg}
                fallbackTone={t.accent}
              />
            </div>
            <div style={{ aspectRatio: "4/5", marginTop: "clamp(24px, 4vw, 48px)" }}>
              <GuestPhoto
                src={p[1] || p[0]}
                style={{ width: "100%", height: "100%" }}
                filter="grayscale(0.55) contrast(1.02)"
                fallbackBg={t.bg}
                fallbackTone={t.accent}
              />
            </div>
          </div>
        </section>
      )}

      <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />

      {/* ────────────────────────────────────────────────────────────────────
          SCHEDULE — numbered editorial list, NOT a grid. Vertical hairline
          rule on the left of each row. Numbers in metric typography.
          ──────────────────────────────────────────────────────────────────── */}
      <section
        ref={scheduleR.ref}
        style={{
          padding: "clamp(72px, 12vw, 160px) clamp(20px, 6vw, 80px)",
          borderTop: `1px solid ${t.ink}1f`,
        }}
      >
        <div
          className="oqshom-schedule-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            columnGap: "clamp(8px, 1.5vw, 24px)",
            ...revealStyle(scheduleR.shown),
          }}
        >
          <div className="oqshom-schedule-head" style={{ gridColumn: "2 / span 3" }}>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.42em",
                color: t.accent,
                marginBottom: 16,
              }}
            >
              {tt("variant.w05.schedule.eyebrow")}
            </div>
            <h2
              style={{
                fontFamily: t.font,
                fontWeight: 400,
                fontSize: "clamp(36px, 5vw, 72px)",
                lineHeight: 0.92,
                letterSpacing: "-0.03em",
                color: t.ink,
                margin: 0,
                fontVariationSettings: '"wght" 400',
              }}
            >
              {tt("variant.w05.schedule.title_1")}
              <br />
              <span style={{ fontStyle: "italic", color: t.accent }}>{tt("variant.w05.schedule.title_2")}</span>
            </h2>
          </div>

          <ol
            className="oqshom-schedule-list"
            style={{
              gridColumn: "6 / span 7",
              listStyle: "none",
              padding: 0,
              margin: 0,
            }}
          >
            {schedule.map(([num, time, label], i) => (
              <li
                key={num}
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  alignItems: "baseline",
                  gap: "clamp(16px, 2.4vw, 32px)",
                  padding: "clamp(20px, 3vw, 32px) 0 clamp(20px, 3vw, 32px) clamp(16px, 2vw, 24px)",
                  borderTop: i === 0 ? `1px solid ${t.ink}1f` : "none",
                  borderBottom: `1px solid ${t.ink}1f`,
                  borderLeft: `1px solid ${t.ink}33`,
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.32em",
                    color: t.accent,
                    fontFeatureSettings: '"lnum", "tnum"',
                    minWidth: "2ch",
                  }}
                >
                  {num}
                </span>
                <span
                  style={{
                    fontFamily: t.font,
                    fontSize: "clamp(22px, 2.6vw, 32px)",
                    fontWeight: 400,
                    color: t.ink,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {label}
                </span>
                <span
                  style={{
                    fontFamily: t.font,
                    fontStyle: "italic",
                    fontSize: "clamp(20px, 2.4vw, 28px)",
                    fontWeight: 400,
                    color: `${t.ink}aa`,
                    fontFeatureSettings: '"lnum", "tnum"',
                    letterSpacing: "0.01em",
                  }}
                >
                  {time}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <div id="oqshom-rsvp">
        <RsvpSection t={t} isDark={isDark} {...ctx.rsvp} />
      </div>

      <ShareSection t={t} />

      {/* ────────────────────────────────────────────────────────────────────
          COLOPHON — small typographic footer block above the standard
          GuestFooter. A nod to editorial publications: set, accent, paper.
          ──────────────────────────────────────────────────────────────────── */}
      <section
        ref={colophonR.ref}
        style={{
          padding: "clamp(48px, 8vw, 96px) clamp(20px, 6vw, 80px) 0",
          borderTop: `1px solid ${t.ink}1f`,
        }}
      >
        <div
          className="oqshom-colophon-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            columnGap: "clamp(8px, 1.5vw, 24px)",
            ...revealStyle(colophonR.shown),
          }}
        >
          <div
            className="oqshom-colophon-mark"
            style={{
              gridColumn: "2 / span 4",
              fontFamily: "var(--font-ui)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.42em",
              color: t.ink,
            }}
          >
            {tt("variant.w05.colophon.title")}
          </div>
          <dl
            className="oqshom-colophon-dl"
            style={{
              gridColumn: "6 / span 6",
              margin: 0,
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              rowGap: 8,
              columnGap: 24,
              fontFamily: "var(--font-ui)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.32em",
              color: `${t.ink}aa`,
            }}
          >
            <dt>{tt("variant.w05.colophon.set_label")}</dt>
            <dd style={{ margin: 0, color: t.ink, fontStyle: "normal" }}>{tt("variant.w05.colophon.set_value")}</dd>
            <dt>{tt("variant.w05.colophon.accent_label")}</dt>
            <dd style={{ margin: 0, color: t.ink }}>{tt("variant.w05.colophon.accent_value")}</dd>
            <dt>{tt("variant.w05.colophon.paper_label")}</dt>
            <dd style={{ margin: 0, color: t.ink }}>{tt("variant.w05.colophon.paper_value")}</dd>
            <dt>{tt("variant.w05.colophon.place_label")}</dt>
            <dd style={{ margin: 0, color: t.ink }}>{t.venue.toUpperCase()}, UZ</dd>
          </dl>
        </div>
      </section>

      <GuestFooter t={t} />
    </div>
  );
}

// ─── Hero photo with desktop-only grayscale-to-color hover ─────────────────
// CSS class + media query — behaviour respects fine-pointer devices and
// reduced-motion. No global styles, no extra deps. The fallback gradient
// uses a single subtle wash from paper to a faint accent tint so empty
// state still feels intentional rather than blank.
function HeroPhoto({
  src,
  fallbackBg,
  fallbackTone,
}: {
  src?: string;
  fallbackBg: string;
  fallbackTone: string;
}) {
  // Stable className per mount — avoids cross-instance bleed.
  const idRef = useRef<string>(`oqshom-hero-${Math.random().toString(36).slice(2, 8)}`);
  const cls = idRef.current;

  const bg = src
    ? `url("${src}")`
    : `linear-gradient(135deg, ${fallbackBg}, ${fallbackTone}22)`;

  return (
    <>
      <style>{`
        .${cls} {
          filter: grayscale(0.85) contrast(1.02);
          transition: filter 600ms ease;
        }
        @media (hover: hover) and (pointer: fine) {
          .${cls}:hover {
            filter: grayscale(0) contrast(1);
            transition: filter 3000ms ease;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .${cls} { transition: none; }
        }
      `}</style>
      <div className="oqshom-hero-photo-frame" style={{ aspectRatio: "21/9", overflow: "hidden" }}>
        <div
          className={`${cls} oqshom-hero-photo-inner`}
          style={{
            width: "100%",
            height: "100%",
            backgroundImage: bg,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      </div>
    </>
  );
}
